/**
 * 4×4 matrices in column-major order (the WebGL / three.js layout), so the
 * render backend can upload them without transposing.
 *
 * Element (row r, column c) lives at index c * 4 + r.
 */
// Explicit extensions: also loaded by build tooling (the asset catalog's model bounds).
import type { Quat } from "./quat.ts";
import { vec3Cross, vec3Dot, vec3Normalize, vec3Sub } from "./vec3.ts";
import type { Vec3 } from "./vec3.ts";

export type Mat4 = readonly number[];

export function mat4Identity(): Mat4 {
  return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
}

/** Translation · Rotation · Scale, like three's `Matrix4.compose`. */
export function mat4Compose(position: Vec3, rotation: Quat, scale: Vec3): Mat4 {
  const [x, y, z, w] = rotation;
  const [sx, sy, sz] = scale;

  const x2 = x + x;
  const y2 = y + y;
  const z2 = z + z;
  const xx = x * x2;
  const xy = x * y2;
  const xz = x * z2;
  const yy = y * y2;
  const yz = y * z2;
  const zz = z * z2;
  const wx = w * x2;
  const wy = w * y2;
  const wz = w * z2;

  return [
    (1 - (yy + zz)) * sx,
    (xy + wz) * sx,
    (xz - wy) * sx,
    0,

    (xy - wz) * sy,
    (1 - (xx + zz)) * sy,
    (yz + wx) * sy,
    0,

    (xz + wy) * sz,
    (yz - wx) * sz,
    (1 - (xx + yy)) * sz,
    0,

    position[0],
    position[1],
    position[2],
    1,
  ];
}

/** Returns `a * b`: transforming by the result applies `b` first, then `a`. */
export function mat4Multiply(a: Mat4, b: Mat4): Mat4 {
  const out = new Array<number>(16);
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      let sum = 0;
      for (let k = 0; k < 4; k++) sum += at(a, row, k) * at(b, k, col);
      out[col * 4 + row] = sum;
    }
  }
  return out;
}

/** General 4×4 inverse by cofactors. Returns null when the matrix is singular (e.g. zero scale). */
export function mat4Invert(m: Mat4): Mat4 | null {
  const [a00, a01, a02, a03, a10, a11, a12, a13, a20, a21, a22, a23, a30, a31, a32, a33] = m as [
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
  ];

  // 2×2 sub-determinants shared by the cofactors below.
  const b00 = a00 * a11 - a01 * a10;
  const b01 = a00 * a12 - a02 * a10;
  const b02 = a00 * a13 - a03 * a10;
  const b03 = a01 * a12 - a02 * a11;
  const b04 = a01 * a13 - a03 * a11;
  const b05 = a02 * a13 - a03 * a12;
  const b06 = a20 * a31 - a21 * a30;
  const b07 = a20 * a32 - a22 * a30;
  const b08 = a20 * a33 - a23 * a30;
  const b09 = a21 * a32 - a22 * a31;
  const b10 = a21 * a33 - a23 * a31;
  const b11 = a22 * a33 - a23 * a32;

  const det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
  if (Math.abs(det) < 1e-12) return null;
  const inv = 1 / det;

  return [
    (a11 * b11 - a12 * b10 + a13 * b09) * inv,
    (a02 * b10 - a01 * b11 - a03 * b09) * inv,
    (a31 * b05 - a32 * b04 + a33 * b03) * inv,
    (a22 * b04 - a21 * b05 - a23 * b03) * inv,
    (a12 * b08 - a10 * b11 - a13 * b07) * inv,
    (a00 * b11 - a02 * b08 + a03 * b07) * inv,
    (a32 * b02 - a30 * b05 - a33 * b01) * inv,
    (a20 * b05 - a22 * b02 + a23 * b01) * inv,
    (a10 * b10 - a11 * b08 + a13 * b06) * inv,
    (a01 * b08 - a00 * b10 - a03 * b06) * inv,
    (a30 * b04 - a31 * b02 + a33 * b00) * inv,
    (a21 * b02 - a20 * b04 - a23 * b00) * inv,
    (a11 * b07 - a10 * b09 - a12 * b06) * inv,
    (a00 * b09 - a01 * b07 + a02 * b06) * inv,
    (a31 * b01 - a30 * b03 - a32 * b00) * inv,
    (a20 * b03 - a21 * b01 + a22 * b00) * inv,
  ];
}

/** Transforms a point, including the perspective divide (three's `Vector3.applyMatrix4`). */
export function mat4TransformPoint(m: Mat4, p: Vec3): Vec3 {
  const w = at(m, 3, 0) * p[0] + at(m, 3, 1) * p[1] + at(m, 3, 2) * p[2] + at(m, 3, 3);
  const invW = w === 0 ? 1 : 1 / w;
  return [
    (at(m, 0, 0) * p[0] + at(m, 0, 1) * p[1] + at(m, 0, 2) * p[2] + at(m, 0, 3)) * invW,
    (at(m, 1, 0) * p[0] + at(m, 1, 1) * p[1] + at(m, 1, 2) * p[2] + at(m, 1, 3)) * invW,
    (at(m, 2, 0) * p[0] + at(m, 2, 1) * p[1] + at(m, 2, 2) * p[2] + at(m, 2, 3)) * invW,
  ];
}

/** The translation column of an affine matrix. */
export function mat4GetTranslation(m: Mat4): Vec3 {
  return [at(m, 0, 3), at(m, 1, 3), at(m, 2, 3)];
}

/**
 * A view matrix (world → camera space) for a camera at `eye` looking at `target`.
 * Like three's cameras, the camera looks down its local -Z axis.
 */
export function mat4LookAt(eye: Vec3, target: Vec3, up: Vec3): Mat4 {
  const zAxis = vec3Normalize(vec3Sub(eye, target));
  const xAxis = vec3Normalize(vec3Cross(up, zAxis));
  const yAxis = vec3Cross(zAxis, xAxis);

  return [
    xAxis[0],
    yAxis[0],
    zAxis[0],
    0,
    xAxis[1],
    yAxis[1],
    zAxis[1],
    0,
    xAxis[2],
    yAxis[2],
    zAxis[2],
    0,
    -vec3Dot(xAxis, eye),
    -vec3Dot(yAxis, eye),
    -vec3Dot(zAxis, eye),
    1,
  ];
}

/** OpenGL-style perspective projection (NDC z in [-1, 1]). `fovY` is in radians. */
/**
 * An orthographic projection centered on the view axis: `height` meters tall,
 * `height × aspect` wide, clipping to [-1, 1] depth like mat4Perspective.
 */
export function mat4Orthographic(height: number, aspect: number, near: number, far: number): Mat4 {
  const halfHeight = height / 2;
  const halfWidth = halfHeight * aspect;
  const rangeInv = 1 / (near - far);

  return [
    1 / halfWidth,
    0,
    0,
    0,
    0,
    1 / halfHeight,
    0,
    0,
    0,
    0,
    2 * rangeInv,
    0,
    0,
    0,
    (far + near) * rangeInv,
    1,
  ];
}

export function mat4Perspective(fovY: number, aspect: number, near: number, far: number): Mat4 {
  const f = 1 / Math.tan(fovY / 2);
  const rangeInv = 1 / (near - far);

  return [
    f / aspect,
    0,
    0,
    0,
    0,
    f,
    0,
    0,
    0,
    0,
    (far + near) * rangeInv,
    -1,
    0,
    0,
    2 * far * near * rangeInv,
    0,
  ];
}

/** Reads element (row, col) from a column-major matrix. */
function at(m: Mat4, row: number, col: number): number {
  return m[col * 4 + row] ?? 0;
}

type Axis = 0 | 1 | 2;

export interface Mat4Parts {
  readonly position: Vec3;
  readonly rotation: Quat;
  readonly scale: Vec3;
}

/**
 * Splits an affine matrix into translation, rotation and scale (the inverse of
 * mat4Compose, like three's `Matrix4.decompose`). A negative determinant is
 * folded into the X scale.
 */
export function mat4Decompose(m: Mat4): Mat4Parts {
  let sx = Math.hypot(at(m, 0, 0), at(m, 1, 0), at(m, 2, 0));
  const sy = Math.hypot(at(m, 0, 1), at(m, 1, 1), at(m, 2, 1));
  const sz = Math.hypot(at(m, 0, 2), at(m, 1, 2), at(m, 2, 2));
  if (determinant3(m) < 0) sx = -sx;

  // Rotation-only 3×3 (row, col), with scale divided out of each column.
  const scale = [sx, sy, sz] as const;
  const r = (row: number, col: Axis) => at(m, row, col) / scale[col];
  return {
    position: mat4GetTranslation(m),
    rotation: quatFromRotation(r),
    scale: [sx, sy, sz],
  };
}

function determinant3(m: Mat4): number {
  const a = (row: number, col: number) => at(m, row, col);
  return (
    a(0, 0) * (a(1, 1) * a(2, 2) - a(1, 2) * a(2, 1)) -
    a(0, 1) * (a(1, 0) * a(2, 2) - a(1, 2) * a(2, 0)) +
    a(0, 2) * (a(1, 0) * a(2, 1) - a(1, 1) * a(2, 0))
  );
}

/** Quaternion from a pure rotation matrix (Shepperd's method, as in three's setFromRotationMatrix). */
function quatFromRotation(r: (row: number, col: Axis) => number): Quat {
  const trace = r(0, 0) + r(1, 1) + r(2, 2);

  if (trace > 0) {
    const s = 0.5 / Math.sqrt(trace + 1);
    return [(r(2, 1) - r(1, 2)) * s, (r(0, 2) - r(2, 0)) * s, (r(1, 0) - r(0, 1)) * s, 0.25 / s];
  }
  if (r(0, 0) > r(1, 1) && r(0, 0) > r(2, 2)) {
    const s = 2 * Math.sqrt(1 + r(0, 0) - r(1, 1) - r(2, 2));
    return [0.25 * s, (r(0, 1) + r(1, 0)) / s, (r(0, 2) + r(2, 0)) / s, (r(2, 1) - r(1, 2)) / s];
  }
  if (r(1, 1) > r(2, 2)) {
    const s = 2 * Math.sqrt(1 + r(1, 1) - r(0, 0) - r(2, 2));
    return [(r(0, 1) + r(1, 0)) / s, 0.25 * s, (r(1, 2) + r(2, 1)) / s, (r(0, 2) - r(2, 0)) / s];
  }
  const s = 2 * Math.sqrt(1 + r(2, 2) - r(0, 0) - r(1, 1));
  return [(r(0, 2) + r(2, 0)) / s, (r(1, 2) + r(2, 1)) / s, 0.25 * s, (r(1, 0) - r(0, 1)) / s];
}
